import React, { useEffect, useRef, useState } from "react";
import icons from "../../../assets/images/constantIcons";
import Table from "../../Common/Table";
import useAddPlayer from "./useAddPlayer";
import AddPlayerForm from "./AddPlayerForm";
import Pagination from "../../Common/Pagination";
import { Link, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const AddPlayer = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const redirectedBookingId = location?.state?.highlightedBookingId;
  const { data, columns, currentRows, totalRows, currentPage, rowsPerPage, filterType, setFilterType, fetchPlayers,
    handlePageChange,
    handleRowsPerPageChange,
    searchQuery,
    setSearchQuery,
    loading,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
  } = useAddPlayer(location?.state?.showPreviousBooking, redirectedBookingId);

  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const openDropdown = () => setDropdownOpen(!dropdownOpen);
  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [selectedFilter, setSelectedFilter] = useState("");

  const handleFilterChange = (type) => {
    setSelectedFilter(type);
    setFilterType(type);
    if (type === "custom") {
      setIsCustomDateOpen(true);
    } else {
      setIsCustomDateOpen(false);
      fetchPlayers(type);
      setDropdownOpen(false);
    }
  };

  const applyFilter = () => {
    if (fromDate && toDate) {
      setFilterType("custom");
      fetchPlayers("custom");
      setDropdownOpen(false);
      setIsCustomDateOpen(false);
    }
  };

  const formatExcelData = (data) => {
    return data?.map((booking) => ({
      "Booking ID": booking.id || "-",
      "Serial Number": booking.serial_number || "-",
      "Table Type": booking.tableType || "-",
      "Table Charge": booking.table_charge || "-",
      Players: Array.isArray(booking.players)
        ? booking.players
          .map((p) => `${p.name} (${p.customer_uniq_id})`)
          .join(", ")
        : booking.players || "-",
      "Payable Players": Array.isArray(booking.payable_players)
        ? booking.payable_players.map((p) => p.name).join(", ")
        : "-",
      "Start Date": booking.start_date || "-",
      "Start Time": booking.start_time || "-",
      "End Date": booking.end_date.split("T")[0] || "-",
      "End Time": booking.end_time || "-",
      Duration: booking.duration || "-",
      "Today Amount": booking.today_amount ?? "-",
      "Total Amount": booking.total_amount ?? "-",
      "Discount": booking.discount ?? "-",
      "Amount Paid": booking.amount_to_be_pay ?? "-",
      "Due Amount": booking.due_amount ?? "-",
    }));
  };

  const capitalize = (str) =>
    str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const downloadExcel = async (rawData) => {
    const formattedData = formatExcelData(rawData);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Bookings");

    // Set columns with capitalized headers
    worksheet.columns = Object.keys(formattedData[0] || {}).map((key) => ({
      header: capitalize(key),
      key,
      width: key.length < 20 ? 25 : key.length + 10,
    }));

    // Style header row: bold + center
    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };

    // Add rows and center content
    formattedData.forEach((rowData) => {
      const row = worksheet.addRow(rowData);
      row.alignment = { horizontal: "center" }; // Content centered, not bold
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
    const time = now
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/:/g, "-")
      .replace(/\s/g, "");

    const fileName = `Booking_${date}_${time}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div className="flex flex-col">
      <div className="flex justify-between">
        <h1 className="main_heading text-2xl xl:text-[30px] mb-3">Booking</h1>
      </div>

      <div className="flex items-center justify-between flex-wrap lg:flex-nowrap">
        <div className="hidden lg:block">
          <button
            className="bg-buttoncolor text-white font-medium text-sm xl:text-base py-2 px-2 rounded-lg w-[120px] xl:w-[125px] text-center"
            onClick={() => {
              toggleModal();
              setFilterType("");
              setSelectedFilter("");
            }}
          >
            Add Booking
          </button>

          <AddPlayerForm
            isOpen={isModalOpen}
            onClose={toggleModal}
            fetchPlayers={fetchPlayers}
          />
        </div>
        <div className="flex gap-2 xl:gap-6 items-center flex-wrap lg:flex-nowrap">
          <p className="lg:hidden block">
            <button
              className="bg-buttoncolor text-white font-medium text-sm xl:text-base py-2 px-2 rounded-lg w-[120px] xl:w-[125px] text-center"
              onClick={() => {
                toggleModal();
                setFilterType("");
                setSelectedFilter("");
              }}
            >
              Add Booking
            </button>

            <AddPlayerForm
              isOpen={isModalOpen}
              onClose={toggleModal}
              fetchPlayers={fetchPlayers}
            />
          </p>
          <p
            className={`text-sm rounded-md cursor-pointer px-2 py-[6px] ${filterType === "previous" ? "font-semibold text-green border border-green" : "border bg-white"
              }`}
            onClick={() => {
              setFilterType("previous");
              setSelectedFilter("");
              fetchPlayers("previous");
              setDropdownOpen(false);
              setIsCustomDateOpen(false);
            }}
          >
            Previous Bookings
          </p>
          <p
            className={`text-sm rounded-md cursor-pointer px-2 py-[6px] ${filterType === "yesterday" ? "font-semibold text-green border border-green" : "border bg-white"
              }`}
            onClick={() => {
              setFilterType("yesterday");
              setSelectedFilter("");
              fetchPlayers("yesterday");
              setDropdownOpen(false);
              setIsCustomDateOpen(false);
            }}
          >
            Yesterday's Bookings
          </p>
          <p
            className={`flex  justify-between items-center py-[6px]  rounded-md cursor-pointer px-2 ${selectedFilter === "custom"
              ? "text-green font-semibold border border-green"
              : "border bg-white"
              }`}
            onClick={() => handleFilterChange("custom")}
          >
            <span className="text-sm">Custom Date</span>
            <icons.MdOutlineCalendarMonth className="text-green700" />
          </p>
          <p
            className="text-sm px-2 py-[6px] rounded-md cursor-pointer border bg-white"
            onClick={() => {
              setFilterType("");
              setSelectedFilter("");
              setFromDate("");
              setToDate("");
              setDropdownOpen(false);
              setIsCustomDateOpen(false);
              fetchPlayers("");
            }}
          >
            Clear Filter
          </p>
          <div className="flex justify-end items-center gap-3">
            {(filterType === "previous" || filterType === "custom") && (
              <div
                onClick={() => downloadExcel(data)}
                className="bg-white text-green rounded-md shadow-sm text-base sm:text-2xl p-2 cursor-pointer"
              >
                <icons.FiDownload />
              </div>
            )}

            <div className="flex items-center border border-gray-300 bg-white px-3 py-2 rounded-lg w-full max-w-[250px]">
              <icons.GoSearch className="text-gray-500 mr-2" />
              <input
                name="search"
                type="text"
                placeholder="Search id, players"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full outline-none bg-transparent text-gray-700 placeholder-gray-400 placeholder:text-xs"
              />
            </div>

          </div>
          {isCustomDateOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
              <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] relative">
                <h1 className="text-G800 text-lg mb-3">Select Custom Date</h1>
                <DatePicker
                  selected={fromDate}
                  onChange={setFromDate}
                  selectsStart
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="From Date"
                  className="text-G500 placeholder:text-xs border px-3 py-2 rounded w-full mb-3 text-sm outline-none"
                  dateFormat="yyyy-MM-dd"
                />
                <DatePicker
                  selected={toDate}
                  onChange={setToDate}
                  selectsEnd
                  startDate={fromDate}
                  endDate={toDate}
                  minDate={fromDate}
                  placeholderText="To Date"
                  className="text-G500 placeholder:text-xs border px-3 py-2 rounded w-full text-sm outline-none"
                  dateFormat="yyyy-MM-dd"
                />

                <div className="flex justify-start space-x-4 mt-6">
                  <button
                    onClick={applyFilter}
                    className="border border-green text-green hover:bg-green hover:text-white py-1 rounded-md w-[75px] text-sm text-center"
                  >
                    Apply
                  </button>
                  <Link to="/mainDashboard/booking" >
                    <button
                      className="border border-green hover:bg-green hover:text-white text-green py-1 rounded-md w-[75px] text-sm text-center"
                      onClick={() => {
                        setFilterType("");
                        setSelectedFilter("");
                        setFromDate("");
                        setToDate("");
                        setDropdownOpen(false);
                        setIsCustomDateOpen(false);
                        fetchPlayers("");
                      }}
                    >
                      Cancel
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center my-4">
          <p className="text-green700 font-medium">Loading data...</p>
        </div>
      ) : (
        <div className="my-2 mb-6 overflow-auto horizontalscroll">
          <Table
            columns={columns}
            data={currentRows}
            highlightedRow={location.state}
          />
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  );
};

export default AddPlayer;
